/**
 * useFocus.js — The Focus / Full switch.
 *
 * Focus hides the caller's Focus set (chosen by an admin from usage data); Full
 * shows everything. Hidden things are only not rendered — routes, deep links and
 * the command palette still reach them.
 */
import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { track } from '../lib/telemetry';

const FOCUS_KEY = ['focus'];

export function useFocus() {
  const { data } = useQuery({
    queryKey: FOCUS_KEY,
    queryFn: () => api.get('/focus'),
    staleTime: 5 * 60_000,
  });
  const mode = data?.mode || 'focus';
  const hiddenSet = useMemo(() => new Set(data?.hidden || []), [data?.hidden]);
  const active = mode === 'focus' && hiddenSet.size > 0;
  const isHidden = useCallback((key) => active && hiddenSet.has(key), [active, hiddenSet]);
  return { mode, hiddenCount: hiddenSet.size, isHidden, members: data?.members };
}

export function useSetFocusMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode) => api.put('/focus/mode', { mode }),
    onMutate: async (mode) => {
      await qc.cancelQueries({ queryKey: FOCUS_KEY });
      const prev = qc.getQueryData(FOCUS_KEY);
      qc.setQueryData(FOCUS_KEY, (d) => ({ ...(d || {}), mode }));
      track('focus.mode', { mode });
      return { prev };
    },
    onError: (_e, _mode, ctx) => qc.setQueryData(FOCUS_KEY, ctx?.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: FOCUS_KEY }),
  });
}

export function useSetFocusHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, hidden }) => api.put(`/focus/${profileId}/hidden`, { hidden }),
    onSettled: () => qc.invalidateQueries({ queryKey: FOCUS_KEY }),
  });
}

export function useFocusUsage(profileId) {
  return useQuery({
    // Own root key: the mode switch cancels and refetches ['focus'] by prefix.
    queryKey: ['focusUsage', profileId],
    queryFn: () => api.get(`/focus/usage/${profileId}`),
    enabled: !!profileId,
    staleTime: 5 * 60_000,
  });
}
